import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import {
  ClipboardList, Plus, Trash2, CheckCircle2, Clock, AlertCircle,
  Users, Flag, Search, Filter, X
} from 'lucide-react';
import axios from 'axios';
import { TaskAttachments } from '../TaskAttachments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PRIORITIES = [
  { id: 'dusuk', label: 'Düşük', badge: 'bg-slate-100 text-slate-700 border-slate-200', dot: 'bg-slate-400', text: 'text-slate-600' },
  { id: 'orta', label: 'Orta', badge: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500', text: 'text-amber-600' },
  { id: 'yuksek', label: 'Yüksek', badge: 'bg-rose-100 text-rose-700 border-rose-200', dot: 'bg-rose-500', text: 'text-rose-600' },
];
const getPriority = (id) => PRIORITIES.find(p => p.id === id) || PRIORITIES[1];

const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('tr-TR'); } catch { return d; }
};
const todayISO = () => new Date().toISOString().split('T')[0];
const isOverdue = (t) => t.status !== 'done' && t.due_date && new Date(t.due_date) < new Date(todayISO());
const initials = (name) => (name || '').split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

import PageHeader from '../ui-pro/PageHeader';
import StatCard from '../ui-pro/StatCard';
const TasksTab = ({ personnelList = [] }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [personFilter, setPersonFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showAssign, setShowAssign] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', due_date: '', priority: 'orta', personnel_ids: [] });

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/personnel-tasks`);
      setTasks(res.data?.items || []);
    } catch { toast.error('Görevler yüklenemedi'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchTasks(); }, []);

  const stats = useMemo(() => {
    const total = tasks.length;
    const done = tasks.filter(t => t.status === 'done').length;
    const pending = tasks.filter(t => t.status !== 'done').length;
    const overdue = tasks.filter(isOverdue).length;
    const byPriority = { dusuk: 0, orta: 0, yuksek: 0 };
    tasks.forEach(t => { byPriority[t.priority || 'orta'] = (byPriority[t.priority || 'orta'] || 0) + 1; });
    return { total, done, pending, overdue, byPriority };
  }, [tasks]);

  // Per-personnel breakdown
  const perPerson = useMemo(() => {
    const map = {};
    tasks.forEach(t => {
      const key = t.personnel_id;
      if (!map[key]) map[key] = { id: key, name: t.personnel_name || 'Bilinmeyen', total: 0, done: 0, pending: 0, overdue: 0, dusuk: 0, orta: 0, yuksek: 0 };
      const p = map[key];
      p.total += 1;
      if (t.status === 'done') p.done += 1; else p.pending += 1;
      if (isOverdue(t)) p.overdue += 1;
      p[t.priority || 'orta'] += 1;
    });
    return Object.values(map).sort((a, b) => b.pending - a.pending || b.total - a.total);
  }, [tasks]);

  const filtered = useMemo(() => {
    let list = tasks;
    if (statusFilter === 'pending') list = list.filter(t => t.status !== 'done');
    else if (statusFilter === 'done') list = list.filter(t => t.status === 'done');
    else if (statusFilter === 'overdue') list = list.filter(isOverdue);
    if (priorityFilter !== 'all') list = list.filter(t => (t.priority || 'orta') === priorityFilter);
    if (personFilter !== 'all') list = list.filter(t => t.personnel_id === personFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(t => (t.title || '').toLowerCase().includes(q) || (t.personnel_name || '').toLowerCase().includes(q));
    }
    // priority order yuksek > orta > dusuk, then pending first
    const prioRank = { yuksek: 0, orta: 1, dusuk: 2 };
    return [...list].sort((a, b) => {
      if ((a.status === 'done') !== (b.status === 'done')) return a.status === 'done' ? 1 : -1;
      return (prioRank[a.priority || 'orta']) - (prioRank[b.priority || 'orta']);
    });
  }, [tasks, statusFilter, priorityFilter, personFilter, search]);

  const handleDelete = async (task) => {
    if (!window.confirm(`Bu görevi silmek istiyor musunuz?\n\n${task.title}`)) return;
    try {
      await axios.delete(`${API}/personnel-tasks/${task.id}`);
      toast.success('Görev silindi');
      fetchTasks();
    } catch { toast.error('Silinemedi'); }
  };

  const togglePerson = (id) => {
    setForm(f => ({ ...f, personnel_ids: f.personnel_ids.includes(id) ? f.personnel_ids.filter(x => x !== id) : [...f.personnel_ids, id] }));
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Görev başlığı zorunlu'); return; }
    if (form.personnel_ids.length === 0) { toast.error('En az bir personel seçin'); return; }
    try {
      const res = await axios.post(`${API}/personnel-tasks/bulk`, {
        personnel_ids: form.personnel_ids,
        title: form.title.trim(),
        description: form.description.trim(),
        due_date: form.due_date || null,
        priority: form.priority,
      });
      toast.success(res.data?.message || 'Görev atandı');
      setShowAssign(false);
      setForm({ title: '', description: '', due_date: '', priority: 'orta', personnel_ids: [] });
      fetchTasks();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Görev atanamadı'); }
  };

  const activePersonnel = personnelList.filter(p => p.active !== false);
  const hasFilters = statusFilter !== 'all' || priorityFilter !== 'all' || personFilter !== 'all' || search;

  return (
    <div className="space-y-4" data-testid="tasks-tab-root">
      <PageHeader
        icon={ClipboardList}
        title="Görev Yönetimi"
        description="Tüm personel görevlerini tek ekrandan takip edin"
        testid="tasks-page-header"
        actions={
          <Button onClick={() => setShowAssign(true)} className="h-9 bg-[#176B91] hover:bg-[#145a7a]" data-testid="tasks-assign-btn">
            <Plus className="w-4 h-4 mr-1" /> Görev Ata
          </Button>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={ClipboardList} tone="indigo" testid="kpi-total-tasks" label="Toplam Görev" value={stats.total} sub="tüm kayıtlar" />
        <StatCard icon={Clock} tone="amber" testid="kpi-pending-tasks" label="Bekleyen" value={stats.pending} sub="devam eden görev" />
        <StatCard icon={CheckCircle2} tone="emerald" testid="kpi-done-tasks" label="Teslim Edilen" value={stats.done} sub="tamamlanan" />
        <StatCard icon={AlertCircle} tone="rose" testid="kpi-overdue-tasks" label="Gecikmiş" value={stats.overdue} sub="süresi geçen" />
      </div>

      {/* Priority summary */}
      <Card><CardContent className="p-3">
        <div className="flex items-center gap-2 mb-2"><Flag className="w-4 h-4 text-slate-500" /><span className="text-xs font-bold uppercase tracking-wider text-slate-700">Öncelik Dağılımı</span></div>
        <div className="grid grid-cols-3 gap-2">
          {PRIORITIES.map(p => (
            <div key={p.id} className={`rounded-lg border p-3 flex items-center justify-between ${p.badge}`} data-testid={`priority-summary-${p.id}`}>
              <div className="flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-full ${p.dot}`} /><span className="text-sm font-medium">{p.label}</span></div>
              <span className="text-lg font-black tabular-nums">{stats.byPriority[p.id] || 0}</span>
            </div>
          ))}
        </div>
      </CardContent></Card>

      {/* Per-personnel breakdown */}
      <Card><CardContent className="p-0">
        <div className="flex items-center gap-2 p-3 border-b bg-slate-50"><Users className="w-4 h-4 text-slate-500" /><span className="text-xs font-bold uppercase tracking-wider text-slate-700">Personele Göre Görev Dağılımı</span></div>
        {perPerson.length === 0 ? (
          <p className="text-center py-8 text-slate-400 text-sm">Henüz görev yok</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="per-person-table">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left text-xs text-slate-600">
                  <th className="px-3 py-2 font-medium">Personel</th>
                  <th className="px-2 py-2 font-medium text-center">Toplam</th>
                  <th className="px-2 py-2 font-medium text-center">Bekleyen</th>
                  <th className="px-2 py-2 font-medium text-center">Teslim</th>
                  <th className="px-2 py-2 font-medium text-center">Gecikmiş</th>
                  <th className="px-2 py-2 font-medium text-center text-rose-600">Yüksek</th>
                  <th className="px-2 py-2 font-medium text-center text-amber-600">Orta</th>
                  <th className="px-2 py-2 font-medium text-center text-slate-500">Düşük</th>
                </tr>
              </thead>
              <tbody>
                {perPerson.map(p => (
                  <tr key={p.id} className="border-b hover:bg-slate-50" data-testid={`person-row-${p.id}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-[#0B172A] flex items-center justify-center text-white text-[10px] font-bold">{initials(p.name)}</div>
                        <span className="font-medium text-slate-800">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-center font-bold">{p.total}</td>
                    <td className="px-2 py-2 text-center"><span className="text-amber-600 font-semibold">{p.pending}</span></td>
                    <td className="px-2 py-2 text-center"><span className="text-emerald-600 font-semibold">{p.done}</span></td>
                    <td className="px-2 py-2 text-center">{p.overdue > 0 ? <span className="text-rose-600 font-bold">{p.overdue}</span> : <span className="text-slate-300">0</span>}</td>
                    <td className="px-2 py-2 text-center">{p.yuksek > 0 ? <Badge className="bg-rose-100 text-rose-700 border-0">{p.yuksek}</Badge> : <span className="text-slate-300">0</span>}</td>
                    <td className="px-2 py-2 text-center">{p.orta > 0 ? <Badge className="bg-amber-100 text-amber-700 border-0">{p.orta}</Badge> : <span className="text-slate-300">0</span>}</td>
                    <td className="px-2 py-2 text-center">{p.dusuk > 0 ? <Badge className="bg-slate-100 text-slate-600 border-0">{p.dusuk}</Badge> : <span className="text-slate-300">0</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent></Card>

      {/* Filters */}
      <Card><CardContent className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Görev veya personel ara..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" data-testid="tasks-search-input" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px] h-9" data-testid="tasks-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Durumlar</SelectItem>
              <SelectItem value="pending">Bekleyen</SelectItem>
              <SelectItem value="done">Teslim Edilen</SelectItem>
              <SelectItem value="overdue">Gecikmiş</SelectItem>
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-[150px] h-9" data-testid="tasks-priority-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Öncelikler</SelectItem>
              {PRIORITIES.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={personFilter} onValueChange={setPersonFilter}>
            <SelectTrigger className="w-[170px] h-9" data-testid="tasks-person-filter"><SelectValue placeholder="Personel" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Personel</SelectItem>
              {perPerson.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={() => { setStatusFilter('all'); setPriorityFilter('all'); setPersonFilter('all'); setSearch(''); }} className="text-slate-500 h-9" data-testid="tasks-clear-filters">
              <X className="w-4 h-4 mr-1" /> Temizle
            </Button>
          )}
          <div className="ml-auto text-sm text-slate-500"><span className="font-semibold text-slate-700">{filtered.length}</span> görev</div>
        </div>
      </CardContent></Card>

      {/* Task list */}
      <Card><CardContent className="p-0">
        {loading ? (
          <p className="text-center py-10 text-slate-400 text-sm">Yükleniyor...</p>
        ) : filtered.length === 0 ? (
          <p className="text-center py-10 text-slate-400 text-sm">Görev bulunamadı</p>
        ) : (
          <div className="divide-y" data-testid="tasks-list">
            {filtered.map(t => {
              const prio = getPriority(t.priority);
              const overdue = isOverdue(t);
              return (
                <div key={t.id} className={`flex items-start gap-3 p-3 ${t.status === 'done' ? 'bg-emerald-50/40' : overdue ? 'bg-rose-50/40' : ''}`} data-testid={`task-item-${t.id}`}>
                  <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${prio.dot}`} title={prio.label} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`font-medium text-sm text-slate-800 ${t.status === 'done' ? 'line-through' : ''}`}>{t.title}</p>
                      <Badge className={`border text-[10px] ${prio.badge}`}>{prio.label}</Badge>
                      {t.status === 'done' ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]"><CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Teslim</Badge>
                      ) : overdue ? (
                        <Badge className="bg-rose-100 text-rose-700 border-0 text-[10px]"><AlertCircle className="w-2.5 h-2.5 mr-0.5" /> Gecikmiş</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]"><Clock className="w-2.5 h-2.5 mr-0.5" /> Bekliyor</Badge>
                      )}
                    </div>
                    {t.description && <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">{t.description}</p>}
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-400 flex-wrap">
                      <span className="text-slate-600 font-medium">{t.personnel_name}</span>
                      <span>Atandı: {formatDate(t.created_at)}</span>
                      {t.due_date && <span className={overdue ? 'text-rose-500 font-medium' : ''}>Son tarih: {formatDate(t.due_date)}</span>}
                      {t.completed_at && <span className="text-emerald-500">Bitti: {formatDate(t.completed_at)}</span>}
                    </div>
                    <TaskAttachments taskId={t.id} canManage />
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(t)} className="h-7 w-7 p-0 shrink-0" data-testid={`task-delete-${t.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent></Card>

      {/* Assign modal */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tasks-assign-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClipboardList className="w-5 h-5 text-indigo-600" /> Görev Ata</DialogTitle>
            <DialogDescription>Bir veya birden fazla personele öncelikli görev atayın</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAssign} className="space-y-4">
            <div><Label>Görev Başlığı *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Örn: Salon araştırması yap" data-testid="assign-title-input" /></div>
            <div><Label>Açıklama</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Detaylar..." data-testid="assign-description-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Son Tarih</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="assign-due-input" /></div>
              <div>
                <Label>Öncelik</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="assign-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        <span className="flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-full ${p.dot}`} /> {p.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Personel Seçimi ({form.personnel_ids.length} seçili)</Label>
                <button type="button" className="text-xs text-indigo-600 hover:underline" onClick={() => setForm(f => ({ ...f, personnel_ids: f.personnel_ids.length === activePersonnel.length ? [] : activePersonnel.map(p => p.id) }))} data-testid="assign-select-all">Tümünü Seç / Kaldır</button>
              </div>
              <div className="grid grid-cols-2 gap-1.5 max-h-52 overflow-y-auto border rounded-lg p-2" data-testid="assign-personnel-list">
                {activePersonnel.map(p => {
                  const checked = form.personnel_ids.includes(p.id);
                  return (
                    <button key={p.id} type="button" onClick={() => togglePerson(p.id)} className={`flex items-center gap-2 text-left text-sm px-2 py-1.5 rounded border transition-all ${checked ? 'bg-indigo-50 border-indigo-400' : 'bg-white border-slate-200 hover:border-indigo-200'}`} data-testid={`assign-person-${p.id}`}>
                      <span className={`w-4 h-4 rounded flex items-center justify-center text-[10px] ${checked ? 'bg-indigo-600 text-white' : 'border border-slate-300'}`}>{checked && '✓'}</span>
                      <span className="truncate">{p.name}{!p.phone && <span className="text-[10px] text-amber-500 ml-1">(tel yok)</span>}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <p className="text-[11px] text-indigo-700">💬 Telefonu olan her personele WhatsApp bildirimi gönderilir.</p>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowAssign(false)} className="flex-1">İptal</Button>
              <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700" data-testid="assign-submit-btn">Görevi Ata</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TasksTab;
